import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

import { dialogWide } from "@/lib/dialogSize";

/**
 * What a deletion driven from an uploaded id list achieved.
 */
/*
 * Counts only, because that is all the event carries: the per-row outcomes live in the run's own report
 * rather than on the audit row, which the audit list projection loads in full for every row it returns.
 */
export const ViewBulkDeletionAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => (
  <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className={`${dialogWide} overflow-hidden`}>
      {auditLog ? (
        <>
          <DialogHeader>
            <DialogTitle>{AuditLogTypeLabels[auditLog.eventType]}</DialogTitle>
          </DialogHeader>

          <Separator />

          <div className="flex flex-col gap-2 overflow-y-auto py-2">
            {auditLog.additionalData &&
              (() => {
                try {
                  const data = JSON.parse(auditLog.additionalData);
                  const failed = (data.FailedCount ?? 0) > 0;

                  return (
                    <>
                      <div className="flex flex-row gap-2 items-center">
                        <span className="text-sm font-bold">Status:</span>
                        <Badge variant={data.Cancelled ? "secondary" : data.Stopped ? "warning" : data.Completed ? (failed ? "warning" : "success") : "neutral"}>
                          {data.Cancelled
                            ? "Cancelled before it ran"
                            : data.Stopped
                              ? "Stopped"
                              : data.Completed ? (failed ? "Finished with failures" : "Completed") : "Started"}
                        </Badge>
                      </div>

                      <LabelValuePair label="Reason" value={data.Reason} />
                      {data.Stopped && <LabelValuePair label="Stopped by" value={data.StoppedBy || "Unknown"} />}
                      <LabelValuePair label="Deleting" value={data.Target} />
                      <LabelValuePair label="Ids name" value={data.Mode} />
                      <LabelValuePair
                        label="SharePoint copies"
                        value={data.RecordsOnly ? "Left in place (records only)" : "Deleted"}
                      />

                      <Separator />

                      <LabelValuePair label="Ids uploaded" value={data.TotalRows ?? 0} />
                      <LabelValuePair label="Deleted" value={data.DeletedCount ?? 0} />
                      <LabelValuePair label="Not found" value={data.NotFoundCount ?? 0} />
                      <LabelValuePair label="Not a valid id" value={data.MalformedCount ?? 0} />
                      <LabelValuePair label="Failed" value={data.FailedCount ?? 0} />
                    </>
                  );
                } catch {
                  return <span className="text-sm">Failed to parse additionalData</span>;
                }
              })()}

            <LabelValuePair label="Organization ID" value={organizationId} />
            <LabelValuePair
              label="Performed By"
              value={auditLog.performerEmailAddress || auditLog.performedBy}
            />
            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </div>
        </>
      ) : (
        <div />
      )}
    </DialogContent>
  </Dialog>
);

export default ViewBulkDeletionAuditLogDetailsModal;
